import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { cartLineKey, useCart } from "@/context/CartContext";
import { useScrollLock } from "@/utils/useScrollLock";
import { formatBRL } from "@/utils/format";
import { createCheckout } from "@/services/store-api";

export default function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalPrice } = useCart();
  useScrollLock(isOpen);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // O pagamento acontece no checkout da Yampi, que coleta os dados da cliente.
  const handleCheckout = async () => {
    if (isRedirecting) return;
    setCheckoutError(null);
    setIsRedirecting(true);
    try {
      window.location.assign(await createCheckout(items));
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : "Não foi possível iniciar o checkout.");
      setIsRedirecting(false);
    }
  };

  return (
    <>
      <div onClick={closeCart} className={`bz-scrim ${isOpen ? "is-open" : ""}`}></div>
      <aside className={`bz-side bz-side-right ${isOpen ? "is-open" : ""}`} aria-label="Sacola" aria-hidden={!isOpen}>
        <div className="bz-side-head">
          <h2>Sacola</h2>
          <button type="button" className="bz-icon-btn" aria-label="Fechar sacola" onClick={closeCart}>
            <X size={20} strokeWidth={1} aria-hidden="true" />
          </button>
        </div>

        <div className="bz-side-body">
          {items.length === 0 ? (
            <div className="bz-bag-empty">
              <p>Sua sacola está vazia.</p>
              <Link to="/produtos" className="bz-link" onClick={closeCart}>Ver as peças</Link>
            </div>
          ) : (
            <ul className="bz-bag-list">
              {items.map((item) => (
                <li key={cartLineKey(item)}>
                  <img src={item.image} alt={item.name} />
                  <div>
                    <p className="bz-bag-name">{item.name}</p>
                    {item.variantLabel && <p className="bz-bag-meta">{item.variantLabel}</p>}
                    <p className="bz-bag-price">{formatBRL(item.price)}</p>
                    <div className="bz-bag-actions">
                      <div className="bz-qty">
                        <button type="button" aria-label="Diminuir quantidade" onClick={() => updateQuantity(cartLineKey(item), item.quantity - 1)}>
                          <Minus size={13} strokeWidth={1.5} aria-hidden="true" />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          aria-label="Aumentar quantidade"
                          onClick={() => updateQuantity(cartLineKey(item), item.quantity + 1)}
                          disabled={item.maxQuantity !== undefined && item.quantity >= item.maxQuantity}
                        >
                          <Plus size={13} strokeWidth={1.5} aria-hidden="true" />
                        </button>
                      </div>
                      <button type="button" className="bz-text-btn" onClick={() => removeItem(cartLineKey(item))}>Remover</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="bz-side-foot">
            <div className="bz-bag-total">
              <span>Subtotal</span>
              <strong>{formatBRL(totalPrice)}</strong>
            </div>
            {checkoutError && <p role="alert" className="bz-form-error">{checkoutError}</p>}
            <button type="button" className="bz-btn" disabled={isRedirecting} onClick={handleCheckout}>
              {isRedirecting ? "Redirecionando..." : "Finalizar compra"}
            </button>
            <p className="bz-side-note">Dados de entrega, frete e pagamento no próximo passo.</p>
          </div>
        )}
      </aside>
    </>
  );
}
